import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import BasicLabel from '../../../../../../components/BasicLabel';
import LazyLoadSelect from '../../../../../../components/LazyLoadSelect';
import ENDPOINT from '../../../../../../constants/endpoints';
import {convertHtmlToEditorState} from '../../../../../../helpers/formik';
import useApi from '../../../../../../hooks/useApi';

const SendMessageFormTemplateSelect = props => {
  const {
    name,
    labelText = 'Load Template',
    placeholder = 'Select a template',
    labelStyles = { fontWeight: 'bold', fontSize: '0.9286rem' },
    onRequestFail = () => {},
    onBeforeSelect = () => {},
    onAfterSelect = () => {},
  } = props;

  const api = useApi();

  const handleSelected = async (id, { values, setValues }) => {
    onBeforeSelect();

    const response = await fetchData(id).catch(onRequestFail);

    if (response) {
      const files = (
        await fetchFiles(
          id,
          response.attachments.map(({ id }) => id)
        )
      ).filter(f => !!f);

      setValues({
        ...values,
        attachments: files,
        attachmentsSize:
          files && files.length > 0
            ? files.reduce((sum, a) => sum + Number(a.file.size), 0)
            : 0,
        subject: response.Subject,
        message: convertHtmlToEditorState(response.Message || ''),
      });
    }

    onAfterSelect();
  };

  const fetchOptions = async (query, offset) =>
    new Promise(resolve =>
      api.get(
        ENDPOINT.FORM.OPTIONS.MANAGE_JOBS_EMAIL_TEMPLATE_FOLLOWUP,
        { query, offset },
        {
          handleComplete: ({ data }) => resolve(data),
        }
      )
    );

  const fetchData = async id =>
    new Promise((resolve, reject) => {
      api.get(
        ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.VIEW,
        { id },
        {
          handleError: response => reject(response),
          handleSuccess: response => resolve(response),
        }
      );
    });

  const fetchFiles = async (templateId, attachmentsIds) =>
    Promise.all(
      attachmentsIds.map(
        async attachId =>
          new Promise(resolve => {
            api.download(
              ENDPOINT.ACTION.EMAIL_TEMPLATE_FOLLOWUP.DOWNLOAD_ATTACH,
              { id: templateId, attachId },
              {
                handleError: () => resolve(),
                handleSuccess: response => {
                  response.file = new File([response.file], response.filename, {
                    type: response.type,
                  });
                  resolve(response);
                },
              }
            );
          })
      )
    ).catch(() => []);

  return (
    <Field name={name}>
      {({ field: { name }, form }) => (
        <>
          <BasicLabel name={name} text={labelText} styles={labelStyles} />

          <LazyLoadSelect
            isValid
            isSearchable
            name={name}
            value={null}
            required={false}
            isMulti={false}
            placeholder={placeholder}
            getOption={fetchOptions}
            onChange={(field, { value }) => handleSelected(value, form)}
            onBlur={form.onBlur}
          />
        </>
      )}
    </Field>
  );
};

SendMessageFormTemplateSelect.propTypes = {
  name: PropTypes.string.isRequired,
  labelText: PropTypes.string,
  placeholder: PropTypes.string,
  labelStyles: PropTypes.object,
  onRequestFail: PropTypes.func,
  onBeforeSelect: PropTypes.func,
  onAfterSelect: PropTypes.func,
};

export default SendMessageFormTemplateSelect;
